<!--
  src/components/DialogTasksBankExcel.vue
  Import tasks into the BANK from an .xlsx file (POST /Admin/TasksBank/Import).

  The one rule that shapes this dialog: import keys off the `task_id` column.
    empty task_id      -> creates a new task
    existing task_id   -> FULL REPLACE of that task (a blank cell CLEARS the field)
    unknown task_id    -> the whole file is rejected and NOTHING is written
    8+ digit task_id   -> ignored; a new task is created and the server returns an
                          old -> new id mapping file, which we download
  So an update row is the task's complete new state, not a patch — which is why the
  warning is always visible and why the safe workflow is export -> edit -> import.

  Import is all-or-nothing: never render partial success. On failure we keep the
  dialog open and list the server's (already Hebrew, display-ready) row errors.
  Excel can neither activate nor retire a task — there is no is_active column.

  Opened from TasksBank.vue's actions ("⋯") menu.
-->
<template>
  <div class="DialogTasksBankExcel" dir="rtl">
    <div class="modal-card" style="width: 620px;">
      <header class="modal-card-head">
        <p class="modal-card-title">ייבוא משימות מאקסל</p>
        <button type="button" class="delete" @click="$emit('close')" />
      </header>

      <section class="modal-card-body">
        <b-field label="פורמט להורדה">
          <b-button :loading="isFormatLoading" @click="downloadFormat">הורד תבנית לדוגמה</b-button>
        </b-field>

        <!-- Always visible, never dismissible: the full-replace semantics are
             destructive and easy to miss on a hand-built file. -->
        <b-message type="is-warning" has-icon :closable="false">
          <div>
            יש להעלות <b>אך ורק קובץ אקסל (xlsx.) במבנה שהמערכת מכירה</b> —
            התבנית שלמעלה או קובץ שיוצא מהמערכת. קובץ במבנה אחר יידחה.
          </div>
          <div>
            ייבוא מחליף את המשימה <b>במלואה</b> —אם קיים תא ריק <b>מוחק</b> את הערך הקיים בשדה.
          </div>
          <div>
            שורה עם <code>task_id</code> ריק יוצרת משימה חדשה.
            <code>task_id</code> שאינו קיים בבנק פוסל את <b>כל</b> הקובץ ולא נשמר דבר.
          </div>
          <div>
            <code>task_id</code> בן <b>8 ספרות ומעלה</b> אינו נשמר — נוצרת משימה חדשה עם מזהה של המערכת,
            ובסיום הייבוא יורד קובץ מיפוי בין המזהה המקורי למזהה החדש.
          </div>
          <div>
            הקובץ ייפסל ולא יישמר דבר אם: יש טקסט בעמודה מספרית
            (<code>task_id</code>, <code>type</code>, <code>sub_type</code>, <code>External_ID</code>,
            <code>subject_id</code>, <code>duration_min</code>);
            אותו <code>task_id</code> או אותו <code>External_ID</code> מופיע בשתי שורות;
            או שמשימה חדשה זהה לשורה אחרת בקובץ או למשימה שכבר קיימת בבנק.
          </div>
          <div>
            דרך העבודה הבטוחה: <b>ייצוא ← עריכה ← ייבוא</b>.
            אין לייבא קובץ שנבנה ידנית עם חלק מהעמודות בלבד.
          </div>
          <div>לא ניתן להפעיל או להשבית משימה דרך אקסל.</div>
        </b-message>

        <b-field label="בחירת קובץ (יש לטעון קובץ אקסל בפורמט שניתן בלבד!)">
          <b-upload v-model="file" accept=".xlsx" class="file-label" @input="onFilePicked">
            <span class="file-cta">
              <b-icon class="file-icon" icon="upload"></b-icon>
              <span class="file-label">בחירת קובץ</span>
            </span>
            <span class="file-name" v-if="file">
              {{ file.name }}
            </span>
          </b-upload>
        </b-field>

        <!-- Failure detail. Stays inside the dialog so the picker and the warning
             remain in view while the admin fixes the sheet. -->
        <div v-if="serverError" class="import__errors">
          <p class="import__errors__title">{{ serverError }}</p>
          <p class="import__errors__note">לא בוצע שום שינוי בבנק המשימות.</p>
          <ul v-if="serverErrors.length" class="import__errors__list">
            <li v-for="(e, i) in serverErrors" :key="i">{{ e }}</li>
          </ul>
        </div>
      </section>

      <footer class="modal-card-foot">
        <b-button :loading="isLoading" :disabled="!file" type="is-primary" label="ייבא" @click="save" />
        <b-button :disabled="isLoading" label="ביטול" @click="$emit('close')" />
      </footer>
    </div>
  </div>
</template>

<script>
import { ToastProgrammatic as Toast } from "buefy";
import TasksService from "../services/TasksService";
import FileService from "../services/FileService";

const GENERIC_ERROR = "לא היה ניתן לבצע את הפעולה, יש לפנות לתמיכה הטכנית.";

export default {
  name: "DialogTasksBankExcel",
  props: ["onSuccess"],
  data() {
    return {
      isLoading: false,
      isFormatLoading: false,
      file: null,
      // The top-level Error string, and the per-row messages (which may be
      // absent even when Error is set — e.g. missing file / missing headers).
      serverError: null,
      serverErrors: [],
    };
  },
  methods: {
    // `accept` only filters the picker's default view — a file can still be chosen
    // via "all files" or drag-drop, so reject the wrong extension up front rather
    // than making the admin wait for a server round trip to be told.
    onFilePicked(file) {
      this.serverError = null;
      this.serverErrors = [];
      if (file && !/\.xlsx$/i.test(file.name)) {
        this.file = null;
        Toast.open({
          type: "is-warning",
          message: "יש להעלות קובץ אקסל מסוג xlsx. בלבד, במבנה שהמערכת מייצרת.",
          duration: 6000,
        });
      }
    },
    // Same call as the "הורדת תבנית" menu item — offered here too so an admin who
    // opened the dialog first doesn't have to back out to get the template.
    downloadFormat() {
      this.isFormatLoading = true;
      TasksService.getTasksBankFormat()
        .then((r) => FileService.saveXlsx(r.data, r.headers, "tasks-bank-format.xlsx"))
        .catch(() => {
          Toast.open({ type: "is-danger", message: "הורדת התבנית נכשלה.", duration: 6000 });
        })
        .finally(() => { this.isFormatLoading = false; });
    },
    save() {
      if (!this.file) return;
      this.isLoading = true;
      this.serverError = null;
      this.serverErrors = [];

      const formData = new FormData();
      formData.append("file", this.file);

      TasksService.importTasksBank(formData)
        .then((r) => {
          const d = r.data || {};
          const created = d.Created != null ? d.Created : d.created;
          const updated = d.Updated != null ? d.Updated : d.updated;
          const message = d.Message || d.message || "הייבוא הושלם.";
          const reassigned = d.Reassigned != null ? d.Reassigned : d.reassigned;
          // Rows that came with an 8+ digit task_id got a new system id — the server
          // sends the old -> new mapping as a base64 .xlsx (only when there are such rows).
          const mappingFile = d.MappingFile || d.mappingFile;
          if (mappingFile) {
            const bytes = Uint8Array.from(atob(mappingFile), (c) => c.charCodeAt(0));
            FileService.saveBlob(
              new Blob([bytes], { type: FileService.XLSX_MIME }),
              d.MappingFileName || d.mappingFileName || "tasks-bank-id-mapping.xlsx",
            );
          }
          this.$emit("close");
          if (this.onSuccess) this.onSuccess();
          // Updated: 0 on a re-imported unchanged file is success, not a problem —
          // keep the copy neutral.
          this.$buefy.dialog.alert({
            title: "ייבוא משימות",
            message: `
              <div>${message}</div>
              <div>נוצרו: ${created || 0} משימות</div>
              <div>עודכנו: ${updated || 0} משימות</div>
              ${reassigned ? `<div>קיבלו מזהה חדש: ${reassigned} משימות (קובץ המיפוי הורד)</div>` : ""}
            `,
            confirmText: "סגור",
          });
        })
        .catch((err) => {
          const d = (err && err.response && err.response.data) || null;
          // The repo is inconsistent about casing on server error keys, so read both.
          const error = d && (d.Error || d.error);
          const errors = (d && (d.Errors || d.errors)) || [];
          if (error) {
            this.serverError = error;
            this.serverErrors = Array.isArray(errors) ? errors : [];
          } else {
            Toast.open({ type: "is-danger", message: GENERIC_ERROR, duration: 8000 });
          }
        })
        .finally(() => { this.isLoading = false; });
    },
  },
};
</script>

<style scoped lang="scss">
.DialogTasksBankExcel {
  code {
    direction: ltr;
    display: inline-block;
  }

  .import__errors {
    margin-top: 14px;
    border: 1px solid #e1583d;
    border-radius: 4px;
    padding: 10px 12px;

    .import__errors__title {
      color: #e1583d;
      font-weight: 600;
    }

    .import__errors__note {
      font-weight: 600;
      margin-top: 4px;
    }

    // The list is one entry per bad row and can get long — scroll it inside the
    // box so the modal never grows past the viewport.
    .import__errors__list {
      margin-top: 8px;
      max-height: 240px;
      overflow-y: auto;
      padding-inline-start: 18px;
      list-style: disc;

      li {
        padding: 2px 0;
      }
    }
  }
}
</style>
